import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiEnvelope } from 'shared';

const REFRESH_AHEAD_MS = 60_000;
const client = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: { Accept: 'application/json' },
});
let token = sessionStorage.getItem('adminAccessToken');
let refreshPromise: Promise<string | null> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
const tokenListeners = new Set<(next: string | null) => void>();

function tokenExpiresAt(value: string): number | null {
  try {
    const [, payload] = value.split('.');
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: unknown;
    };
    return typeof decoded.exp === 'number' ? decoded.exp * 1_000 : null;
  } catch {
    return null;
  }
}
function clearRefreshTimer(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}
function scheduleTokenRefresh(): void {
  clearRefreshTimer();
  if (!token) return;
  const expiresAt = tokenExpiresAt(token);
  if (!expiresAt) return;
  refreshTimer = setTimeout(
    () => {
      void refreshAdminToken();
    },
    Math.max(0, expiresAt - Date.now() - REFRESH_AHEAD_MS),
  );
}

export function setAdminToken(next: string | null): void {
  token = next;
  if (next) sessionStorage.setItem('adminAccessToken', next);
  else sessionStorage.removeItem('adminAccessToken');
  scheduleTokenRefresh();
  tokenListeners.forEach((listener) => listener(next));
}
export function onAdminTokenChange(listener: (next: string | null) => void): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

client.interceptors.request.use((config) => {
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as
      (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const payload = error.response?.data as { code?: unknown; message?: unknown } | undefined;
    if (
      error.response?.status === 401 &&
      !(typeof payload?.code === 'string' && payload.code.startsWith('RUNTIME_')) &&
      request &&
      !request._retried &&
      !request.url?.startsWith('/admin/auth/')
    ) {
      request._retried = true;
      const refreshed = await refreshAdminToken();
      if (refreshed) {
        request.headers.Authorization = `Bearer ${refreshed}`;
        return client.request(request);
      }
    }
    return Promise.reject(Object.assign(
      new Error(typeof payload?.message === 'string' ? payload.message : error.message),
      { status: error.response?.status ?? 0, code: payload?.code },
    ));
  },
);

export async function refreshAdminToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post<ApiEnvelope<{ accessToken: string }>>(
        '/api/v1/admin/auth/refresh',
        {},
        { withCredentials: true },
      )
      .then((response) => {
        const next = response.data.data.accessToken;
        setAdminToken(next);
        return next;
      })
      .catch(() => {
        setAdminToken(null);
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

scheduleTokenRefresh();

export const adminApi = {
  async login(email: string, password: string, captchaPayload?: string) {
    return (
      await client.post<ApiEnvelope<{ accessToken: string }>>('/admin/auth/login', {
        email,
        password,
        captchaPayload,
      })
    ).data.data;
  },
  async logout(): Promise<void> {
    await client.post('/admin/auth/logout');
  },
  async me() {
    return (await client.get<ApiEnvelope<unknown>>('/admin/auth/me')).data.data;
  },
  async get<T>(url: string): Promise<T> {
    return (await client.get<ApiEnvelope<T>>(url)).data.data;
  },
  async post<T>(url: string, body: unknown): Promise<T> {
    return (await client.post<ApiEnvelope<T>>(url, body)).data.data;
  },
  async patch<T>(url: string, body: unknown): Promise<T> {
    return (await client.patch<ApiEnvelope<T>>(url, body)).data.data;
  },
  async upload<T>(url: string, file: File): Promise<T> {
    const body = new FormData();
    body.append('image', file);
    return (await client.post<ApiEnvelope<T>>(url, body)).data.data;
  },
  async delete<T>(url: string, body?: unknown): Promise<T> {
    return (await client.delete<ApiEnvelope<T>>(url, { data: body })).data.data;
  },
};
